import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listNotifications,
  markRead,
  markAllRead,
  type AppNotification,
} from "../lib/notificationApi";
import { useAuth } from "./useAuth";

const POLL_INTERVAL = 30_000; // 30 seconds

export function useNotifications() {
  const queryClient = useQueryClient();
  const { activeBusinessId } = useAuth();
  const queryKey = ["notifications", activeBusinessId ?? "global"] as const;

  const query = useQuery({
    queryKey,
    queryFn: () => listNotifications(50, undefined, activeBusinessId ?? undefined),
    refetchInterval: POLL_INTERVAL,
    staleTime: 0, // always consider stale so polling works correctly
  });

  const markReadMutation = useMutation({
    mutationFn: (notif: AppNotification) => markRead(notif.SK),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return {
    notifications: query.data?.items ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading,
    markRead: (notif: AppNotification) => markReadMutation.mutate(notif),
    markAllRead: () => markAllReadMutation.mutate(),
  };
}
